import React,{useState} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackButtonClick } from '../utils/tracking';

const {FiCheck,FiX,FiDollarSign,FiShield,FiClock,FiSliders}=FiIcons;

const ComparisonPage=()=> {
  const [petAge,setPetAge]=useState(2);
  const [petType,setPetType]=useState('dog');
  const [location,setLocation]=useState('los-angeles');

  const plans=[
    {name: 'Major Medical (Accident Only)',price: {dog: 25,cat: 15},popular: false,features: [
      'Emergency accidents','Surgery coverage','Hospitalization','Medications','No illness coverage','No wellness care'
    ],reimbursement: '80-90%',deductible: '$100-$1,000',annualLimit: '$5,000',color: 'gray'},
    {name: 'Accident & Illness',price: {dog: 61,cat: 25},popular: true,features: [
      'All accident coverage','Illness coverage','Hereditary conditions','Cancer treatment','Chronic conditions','Diagnostic tests','Alternative therapies'
    ],reimbursement: '80-90%',deductible: '$100-$1,000',annualLimit: '$5,000',color: 'blue'},
    {name: 'Whole Pet with Wellness',price: {dog: 81,cat: 45},popular: false,features: [
      'All illness & accident coverage','Wellness care included','Preventive treatments','Vaccinations','Dental cleanings','Spay/neuter coverage','Behavioral therapy'
    ],reimbursement: '80-90%',deductible: '$100-$1,000',annualLimit: '$5,000 + $250-$450 wellness',color: 'green'}
  ];

  const competitors=[
    {company: 'Nationwide',rating: 4.5,reimbursement: '90%',coverage: 'Excellent',claims: '10-30 days',highlights: ['No age restrictions','Exotic pets covered','Behavioral therapy','Direct vet payment']},
    {company: 'Petplan',rating: 4.2,reimbursement: '80%',coverage: 'Good',claims: '15-30 days',highlights: ['Good hereditary coverage','Limited exotic pets','No behavioral therapy']},
    {company: 'Healthy Paws',rating: 4.0,reimbursement: '80%',coverage: 'Good',claims: '2-5 days',highlights: ['Fast claims','No wellness option','Age restrictions']},
    {company: 'ASPCA',rating: 3.8,reimbursement: '70%',coverage: 'Fair',claims: '10-15 days',highlights: ['Lower reimbursement','Basic coverage','Limited options']}
  ];

  const locationOptions=[
    {value: 'los-angeles',label: 'Los Angeles'},
    {value: 'san-francisco',label: 'San Francisco'},
    {value: 'san-diego',label: 'San Diego'},
    {value: 'sacramento',label: 'Sacramento'},
    {value: 'san-jose',label: 'San Jose'},
    {value: 'fresno',label: 'Fresno'},
    {value: 'oakland',label: 'Oakland'},
    {value: 'anaheim',label: 'Anaheim'}
  ];

  const calculatePrice=(basePrice,age,location)=> {
    let price=basePrice[petType];

    // Age multiplier (no upper age limits per manual)
    if (age < 1) price *=0.8;
    else if (age > 7) price *=1.4;
    else if (age > 4) price *=1.2;

    // Location multiplier (CA urban areas 10-20% higher per manual)
    const locationMultipliers={
      'los-angeles': 1.2,
      'san-francisco': 1.3,
      'san-diego': 1.1,
      'sacramento': 1.0,
      'san-jose': 1.25,
      'fresno': 0.9,
      'oakland': 1.15,
      'anaheim': 1.1
    };
    price *=locationMultipliers[location] || 1.0;

    return Math.round(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Compare Nationwide Pet Insurance Plans in California
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find the perfect coverage for your pet with our detailed comparison tool. Get personalized quotes for dogs,cats,birds,and exotic pets.
          </p>
        </motion.div>

        {/* Pricing Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiSliders} className="h-6 w-6 mr-3 text-blue-600" />
            Personalized Pricing Calculator
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pet Type</label>
              <select
                value={petType}
                onChange={(e) => setPetType(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="dog">Dog</option>
                <option value="cat">Cat</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pet Age</label>
              <input
                type="range"
                min="0"
                max="15"
                value={petAge}
                onChange={(e) => setPetAge(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-1">
                {petAge} {petAge === 1 ? 'year' : 'years'} old
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">California Location</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {locationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <a
                href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackButtonClick('Compare Calculator Get Official Quote')}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
              >
                Get Official Quote
              </a>
            </div>
          </div>
        </motion.div>

        {/* Plans Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Choose Your Perfect Plan
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {plans.map((plan,index) => (
              <div
                key={index}
                className={`relative bg-white rounded-lg shadow-lg overflow-hidden ${
                  plan.popular ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className={`p-6 ${plan.popular ? 'pt-12' : ''}`}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900">
                      ${calculatePrice(plan.price,petAge,location)}
                      <span className="text-lg font-normal text-gray-600">/month</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Estimated for {petAge} year old {petType} in{' '}
                      {locationOptions.find(l => l.value === location)?.label}
                    </div>
                  </div>
                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature,featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <SafeIcon
                          icon={feature.includes('No ') ? FiX : FiCheck}
                          className={`h-5 w-5 ${
                            feature.includes('No ') ? 'text-red-500' : 'text-green-500'
                          }`}
                        />
                        <span className={feature.includes('No ') ? 'text-gray-500' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 gap-4 mb-6 text-sm">
                    <div>
                      <div className="font-semibold text-gray-900">Reimbursement</div>
                      <div className="text-gray-600">{plan.reimbursement}</div>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Deductible</div>
                      <div className="text-gray-600">{plan.deductible}</div>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Annual Limit</div>
                      <div className="text-gray-600">{plan.annualLimit}</div>
                    </div>
                  </div>
                  <a
                    href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackButtonClick(`Compare Select Plan`, { plan_name: plan.name })}
                    className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Select This Plan
                  </a>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Competitor Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden mb-12"
        >
          <div className="p-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How Nationwide Compares to Other Pet Insurance Companies
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Company</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Rating</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Max Reimbursement</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Coverage Quality</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Claims Processing</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Key Features</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((competitor,index) => (
                    <tr
                      key={index}
                      className={`border-b border-gray-100 ${
                        competitor.company === 'Nationwide' ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="py-4 px-4">
                        <div className="font-semibold text-gray-900">{competitor.company}</div>
                        {competitor.company === 'Nationwide' && (
                          <div className="text-sm text-blue-600">America's #1 Pet Insurer</div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-1">
                          <span className="font-semibold">{competitor.rating}</span>
                          <span className="text-yellow-400">★</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 font-semibold">{competitor.reimbursement}</td>
                      <td className="py-4 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-sm ${
                            competitor.coverage === 'Excellent'
                              ? 'bg-green-100 text-green-800'
                              : competitor.coverage === 'Good'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {competitor.coverage}
                        </span>
                      </td>
                      <td className="py-4 px-4">{competitor.claims}</td>
                      <td className="py-4 px-4">
                        <ul className="text-sm space-y-1">
                          {competitor.highlights.map((highlight,hIndex) => (
                            <li key={hIndex} className="text-gray-600">• {highlight}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* California-Specific Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg p-8 mb-12"
        >
          <h2 className="text-3xl font-bold mb-6">Pet Insurance for California Pet Owners</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Why California Needs Pet Insurance</h3>
              <ul className="space-y-2">
                <li>• High veterinary costs in major cities like LA and SF</li>
                <li>• Active outdoor lifestyle increases accident risk</li>
                <li>• Excellent veterinary care available statewide</li>
                <li>• No state restrictions on pet insurance</li>
                <li>• Multi-pet discounts available (5-10% off)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">California Coverage Highlights</h3>
              <ul className="space-y-2">
                <li>• Works with all licensed CA veterinarians</li>
                <li>• Coverage for earthquake-related injuries</li>
                <li>• Specialized exotic pet coverage (popular in CA)</li>
                <li>• Alternative therapy coverage (acupuncture,etc.)</li>
                <li>• Direct vet payment option available</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a
              href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackButtonClick('Compare CA Info Get Quote')}
              className="bg-yellow-500 text-blue-900 px-8 py-3 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors inline-block"
            >
              Get Your California Quote Now
            </a>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Protect Your Pet?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get an instant quote and start coverage as soon as tomorrow with America's #1 pet insurance provider.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackButtonClick('Compare CTA Get Instant Quote')}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Get Instant Quote
            </a>
            <Link
              to="/quote"
              onClick={() => trackButtonClick('Compare CTA Request Information')}
              className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
            >
              Request Information
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
export default ComparisonPage;